import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { num } from "@owners/format";
import { api } from "../api";
import { DocLinks, FOLDER_KEYS, FOLDER_LABELS, StatusPill, periodLabel, shortDate, uploadFile, type Item } from "../components/Items";

interface HotelList { hotels: { id: string; name: string; needsUpload: number }[] }
interface FileRow { id: string; title: string; fileName: string; docType: string | null; period: string | null; size: number; version: number; uploadedAt: string; uploadedBy: string | null; expiresOn: string | null; issuedBy: string | null }
interface FolderView { hotel: { id: string; name: string }; folder: string; path: string; items: Item[]; files: FileRow[]; subfolders: string[] }

/**
 * Dataroom (SPEC §20) — HMA Users' Dropbox-style repository. Five system
 * folders per hotel feed Owners Module pages; files in folders the user creates
 * are kept but never feed a page. Deep link from Missing Data:
 *   /dataroom?hotel=…&folder=…&item=<requirementId>  (that item is highlighted)
 */
export function Dataroom() {
  const [params, setParams] = useSearchParams();
  const hotels = useQuery({ queryKey: ["dr-hotels"], queryFn: () => api<HotelList>("/api/dataroom/hotels") });
  const hotelId = params.get("hotel") ?? hotels.data?.hotels[0]?.id ?? "";
  const folder = params.get("folder") ?? "financial_reports";
  const path = params.get("path") ?? "";
  const go = (p: Record<string, string>) => setParams({ hotel: hotelId, folder, ...(path ? { path } : {}), ...p });
  const totalNeeds = hotels.data?.hotels.reduce((s, h) => s + h.needsUpload, 0) ?? 0;

  return (
    <>
      <div className="eyebrow">Owners Module · Dataroom</div>
      <h1>Dataroom</h1>
      <p className="muted">{num(totalNeeds)} files needed across your hotels. Files in folders you create are kept here but don't feed the Owners Module.</p>
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 18, alignItems: "start" }}>
        <div className="card" style={{ padding: 8 }}>
          {hotels.data?.hotels.map((h) => (
            <button key={h.id} onClick={() => setParams({ hotel: h.id, folder })}
              style={{ all: "unset", cursor: "pointer", display: "flex", justifyContent: "space-between", padding: "8px 10px", width: "calc(100% - 20px)",
                       borderRadius: 4, background: h.id === hotelId ? "var(--cream)" : undefined, fontWeight: h.id === hotelId ? 600 : 400 }}>
              <span>{h.name}</span>
              {h.needsUpload > 0 && <span className="pill" style={{ color: "#b42318" }}>⚠️ {num(h.needsUpload)}</span>}
            </button>
          ))}
        </div>
        {hotelId && <FolderPane hotelId={hotelId} folder={folder} path={path} highlight={params.get("item")}
          onFolder={(f) => setParams({ hotel: hotelId, folder: f })} onPath={(p) => go(p ? { path: p } : { path: "" })} />}
      </div>
    </>
  );
}

function FolderPane({ hotelId, folder, path, highlight, onFolder, onPath }: {
  hotelId: string; folder: string; path: string; highlight: string | null;
  onFolder: (f: string) => void; onPath: (p: string) => void;
}) {
  const qc = useQueryClient();
  const key = ["dr-folder", hotelId, folder, path];
  const v = useQuery({ queryKey: key, queryFn: () => api<FolderView>(`/api/dataroom/hotels/${hotelId}?folder=${folder}&path=${encodeURIComponent(path)}`) });
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [newFolder, setNewFolder] = useState("");
  const [drag, setDrag] = useState(false);
  const [versionsOf, setVersionsOf] = useState<string | null>(null);
  const hlRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => { hlRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }); }, [v.data, highlight]);

  async function upload(file: File, fields: Record<string, string | undefined>, tag: string) {
    setBusy(tag); setErr(null);
    try {
      await uploadFile(hotelId, file, { folder, ...fields });
      await Promise.all([qc.invalidateQueries({ queryKey: ["dr-folder"] }), qc.invalidateQueries({ queryKey: ["dr-hotels"] }), qc.invalidateQueries({ queryKey: ["missing-data"] })]);
    } catch (e: any) { setErr(e.message); } finally { setBusy(null); }
  }
  const crumbs = path ? path.split("/") : [];

  return (
    <div>
      <div className="tabs">
        {FOLDER_KEYS.map((f) => <button key={f} className={f === folder ? "on" : ""} onClick={() => onFolder(f)}>{FOLDER_LABELS[f]}</button>)}
      </div>
      <div className="row" style={{ marginBottom: 10 }}>
        <button className="btn link" onClick={() => onPath("")}>{v.data?.hotel.name ?? "…"} / {FOLDER_LABELS[folder]}</button>
        {crumbs.map((c, i) => <span key={i}>/ <button className="btn link" onClick={() => onPath(crumbs.slice(0, i + 1).join("/"))}>{c}</button></span>)}
      </div>
      {err && <p className="error">{err}</p>}

      {!path && (v.data?.items.length ?? 0) > 0 && (
        <div className="card">
          <h2>Files this folder needs</h2>
          <table>
            <thead><tr><th>Item</th><th>Status</th><th>Uploaded by</th><th>Modified</th><th>Version</th><th /></tr></thead>
            <tbody>
              {v.data!.items.map((i) => (
                <tr key={i.requirementId} ref={i.requirementId === highlight ? hlRef : undefined}
                    style={i.requirementId === highlight ? { outline: "2px solid var(--gold)" } : undefined}>
                  <td>{i.document ? "" : "⚠️ "}{i.label}{i.period ? ` · ${periodLabel(i.period)}` : ""}</td>
                  <td><StatusPill s={i.status} dueOn={i.dueOn} /></td>
                  <td>{i.status === "calculated" ? "Owners Module" : i.document?.uploadedBy ?? "Missing"}</td>
                  <td>{i.document ? shortDate(i.document.uploadedAt) : i.status === "calculated" ? "—" : "Missing"}</td>
                  <td>{i.document ? <button className="btn link" onClick={() => setVersionsOf(i.document!.id)}>v{i.document.version}</button> : "—"}</td>
                  <td className="row" style={{ gap: 8 }}>
                    {i.document && <DocLinks id={i.document.id} />}
                    {i.status !== "calculated" && (
                      <ItemUpload busy={busy === i.requirementId} needsExpiry={folder === "compliance" && i.docType !== "certificate_of_occupancy" && i.docType !== "contracts"}
                        label={i.document ? "Replace" : "Upload"}
                        onFile={(file, extra) => upload(file, { docType: i.docType, period: i.period ?? undefined, ...extra }, i.requirementId)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) void upload(f, { path }, "drop"); }}
        style={drag ? { outline: "2px dashed var(--gold)" } : undefined}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 style={{ margin: 0 }}>{path ? path.split("/").pop() : "Other files"}</h2>
          <div className="row">
            <input placeholder="New folder name" value={newFolder} onChange={(e) => setNewFolder(e.target.value.replace(/[\\/]|\.\./g, ""))} style={{ width: 160 }} />
            <button className="btn secondary" disabled={!newFolder.trim()} onClick={() => { onPath(path ? `${path}/${newFolder.trim()}` : newFolder.trim()); setNewFolder(""); }}>New folder</button>
            <label className="btn" style={{ cursor: "pointer" }}>
              {busy === "drop" ? "Uploading…" : "Upload"}
              <input type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f, { path }, "drop"); e.target.value = ""; }} />
            </label>
          </div>
        </div>
        <p className="muted" style={{ fontSize: 12 }}>Drag files here. {path ? "Files in folders you create don't feed the Owners Module." : "To fill a required item, use its Upload button above."}</p>
        {v.data?.subfolders.map((s) => (
          <div key={s}><button className="btn link" onClick={() => onPath(path ? `${path}/${s}` : s)}>📁 {s}</button></div>
        ))}
        <table>
          <tbody>
            {v.data?.files.filter((f) => path || !f.docType).map((f) => (
              <tr key={f.id}>
                <td>{f.fileName}</td><td className="muted">{f.uploadedBy ?? ""}</td><td>{shortDate(f.uploadedAt)}</td>
                <td className="num">{num(Math.ceil(f.size / 1024))} KB</td><td><DocLinks id={f.id} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {versionsOf && <Versions id={versionsOf} onClose={() => setVersionsOf(null)} />}
    </div>
  );
}

function ItemUpload({ busy, label, needsExpiry, onFile }: { busy: boolean; label: string; needsExpiry: boolean; onFile: (f: File, extra: Record<string, string | undefined>) => void }) {
  const [expires, setExpires] = useState("");
  return (
    <span className="row" style={{ gap: 6 }}>
      {needsExpiry && <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} title="Expires / next due" style={{ width: 140 }} />}
      <label className="btn secondary" style={{ cursor: "pointer", padding: "3px 10px" }}>
        {busy ? "Uploading…" : label}
        <input type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f, { expiresOn: expires || undefined }); e.target.value = ""; }} />
      </label>
    </span>
  );
}

function Versions({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ["versions", id], queryFn: () => api<{ versions: { id: string; version: number; fileName: string; status: string; uploadedAt: string; uploadedBy?: string }[] }>(`/api/documents/${id}/versions`) });
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Version history">
        <h2>Version history</h2>
        <table>
          <tbody>
            {q.data?.versions.map((x) => (
              <tr key={x.id}>
                <td>v{x.version}</td><td>{x.fileName}</td><td className="muted">{x.uploadedBy ?? ""}</td>
                <td>{shortDate(x.uploadedAt)}</td><td>{x.status === "active" ? "Current" : "Replaced"}</td><td><DocLinks id={x.id} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ justifyContent: "flex-end" }}><button className="btn secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}
